import { apiClient } from './client'
import { session } from './session'
import type { AuthSession, LoginOutcome, SignInEnrolmentResponse } from './types'

export interface LoginRequest {
  username: string
  password: string
}

/**
 * First factor. A successful password check does not by itself produce a session: users holding
 * approval, administration, or teller permissions are answered with `mfa_required` and must
 * complete the TOTP challenge, and a user flagged for a forced password change is answered with
 * `password_change_required`. The challenge token returned in those two cases is short-lived and
 * authorises exactly one follow-up call.
 */
export async function login(request: LoginRequest): Promise<LoginOutcome> {
  const response = await apiClient.post<LoginOutcome>('/auth/login', request)
  return response.data
}

export interface TotpChallengeRequest {
  challengeToken: string
  /** Six digits from the authenticator app. No SMS fallback exists for staff — SIM-swap risk. */
  code: string
}

export async function verifyTotp(request: TotpChallengeRequest): Promise<LoginOutcome> {
  const response = await apiClient.post<LoginOutcome>('/auth/mfa', request)
  return response.data
}

/**
 * First-sign-in authenticator enrolment. MFA is mandatory before a session exists for anyone
 * holding an approval, administration, or teller permission, so a brand-new account cannot reach
 * the ordinary (bearer-authenticated) enrolment route — sign-in answers `mfa_enrolment_required`
 * with a token that authorises exactly this pair of calls.
 */
export async function beginMfaEnrolment(challengeToken: string): Promise<SignInEnrolmentResponse> {
  const response = await apiClient.post<SignInEnrolmentResponse>('/auth/mfa/enrol', { challengeToken })
  return response.data
}

/** Proves a code from the new authenticator, which activates the secret and completes sign-in. */
export async function confirmMfaEnrolment(request: TotpChallengeRequest): Promise<LoginOutcome> {
  const response = await apiClient.post<LoginOutcome>('/auth/mfa/enrol/confirm', request)
  return response.data
}

export interface ChangePasswordRequest {
  /** Present when the change is forced at sign-in; absent when a signed-in user changes theirs. */
  challengeToken?: string
  currentPassword: string
  newPassword: string
}

export async function changePassword(request: ChangePasswordRequest): Promise<LoginOutcome> {
  const response = await apiClient.post<LoginOutcome>('/auth/password/change', request)
  return response.data
}

/**
 * Ends the session on the server: the refresh-token family is revoked and its cookie cleared.
 * Dropping the in-memory access token alone would leave a usable refresh cookie behind.
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout')
  } finally {
    session.clear()
  }
}

export const isAuthenticatedOutcome = (
  outcome: LoginOutcome,
): outcome is { outcome: 'authenticated' } & AuthSession => outcome.outcome === 'authenticated'
